<template>
  <div class="devices-layout">
    <EventSidebar :event-id="eventId" />
    <main class="devices-main">
      <header class="devices-header">
        <p class="eyebrow">CEREMONY DEVICES</p>
        <h1>Connected devices</h1>
        <p>See connected browsers and devices that have lost contact.</p>
      </header>
      <section class="devices-panel" aria-label="Connected ceremony screens">
        <div class="devices-summary">
          <span v-if="!error && !loading" class="device-count">{{ connectedCount }} connected · {{ devices.length - connectedCount }} offline</span>
          <span v-else>{{ loading ? 'Checking connections…' : 'Connection check unavailable' }}</span>
          <span class="this-browser">This browser: <strong>{{ browser.label }}</strong></span>
        </div>
        <p v-if="error" class="devices-error" role="alert">{{ error }}</p>
        <p v-else-if="loading" class="devices-empty" role="status">Looking for connected ceremony screens…</p>
        <table v-else-if="devices.length">
          <thead><tr><th scope="col">Device</th><th scope="col">Screen</th><th scope="col">Connection</th></tr></thead>
          <tbody>
            <tr v-for="device in devices" :key="device.deviceId">
              <td><span :class="['connection-dot', { offline: device.status === 'offline' }]" aria-hidden="true"></span><strong>{{ device.label }}</strong><span v-if="device.deviceId === browser.id" class="you-label">This browser</span></td>
              <td>{{ device.screens.join(', ') }}<small v-if="device.status === 'offline'" class="last-seen">Last used</small></td>
              <td><strong :class="device.status === 'offline' ? 'offline-status' : 'connected-status'">{{ device.status === 'offline' ? 'Offline' : 'Connected' }}</strong><small v-if="device.status === 'offline'" class="last-seen">Last seen {{ lastSeenText(device.secondsSinceLastSeen) }}</small></td>
            </tr>
          </tbody>
        </table>
        <p v-else class="devices-empty">No ceremony devices seen yet. Open Queue, Presenter, Lower Thirds or Participants on a device to see it here.</p>
        <p class="devices-footnote">Updates automatically. Devices show Offline within about 45 seconds of losing contact and stay listed for this ceremony. Connected does not mean the screen is in sync.</p>
      </section>
    </main>
  </div>
</template>

<script>
import EventSidebar from "./EventSidebar.vue";
import { fetchConnectedDevices } from "@/lib/api";
import { getBrowserIdentity } from "@/lib/deviceIdentity";

export default {
  name: "ConnectedDevicesView",
  components: { EventSidebar },
  data() {
    return { devices: [], loading: true, error: "", browser: getBrowserIdentity(), timer: null, requestVersion: 0, stopped: false };
  },
  computed: {
    eventId() { return this.$route.params.eventId; },
    connectedCount() { return this.devices.filter(device => device.status === "connected").length; },
  },
  watch: {
    eventId() {
      this.requestVersion++;
      window.clearTimeout(this.timer);
      this.devices = []; this.loading = true; this.error = "";
      this.refreshDevices();
    },
  },
  methods: {
    lastSeenText(seconds) {
      if (seconds < 60) return `${seconds} seconds ago`;
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
      const days = Math.floor(hours / 24);
      return `${days} ${days === 1 ? 'day' : 'days'} ago`;
    },
    visibilityChanged() {
      this.requestVersion++;
      window.clearTimeout(this.timer);
      this.devices = []; this.loading = true;
      if (!document.hidden) this.refreshDevices();
    },
    async refreshDevices() {
      if (document.hidden || this.stopped) return;
      const version = ++this.requestVersion;
      const eventId = this.eventId;
      try {
        const result = await fetchConnectedDevices(eventId);
        if (this.stopped || version !== this.requestVersion) return;
        if (result.eventId !== eventId || !Array.isArray(result.devices) || result.devices.some(device => !["connected", "offline"].includes(device.status) || !Number.isInteger(device.secondsSinceLastSeen) || device.secondsSinceLastSeen < 0)) throw new Error("Invalid devices response");
        this.devices = result.devices; this.error = "";
      } catch {
        if (this.stopped || version !== this.requestVersion) return;
        this.devices = [];
        this.error = "Cannot check connections right now. Retrying automatically…";
      } finally {
        if (!this.stopped && version === this.requestVersion) {
          this.loading = false;
          this.timer = window.setTimeout(() => this.refreshDevices(), 5000);
        }
      }
    },
  },
  mounted() { document.addEventListener("visibilitychange", this.visibilityChanged); this.refreshDevices(); },
  beforeUnmount() {
    this.stopped = true; this.requestVersion++; window.clearTimeout(this.timer);
    document.removeEventListener("visibilitychange", this.visibilityChanged);
  },
};
</script>

<style scoped>
.devices-layout { display: flex; gap: 1.5rem; padding: 1.5rem; align-items: flex-start; }
.devices-main { flex: 1; min-width: 0; max-width: 1050px; }
.devices-header { margin: 0.5rem 0 1.75rem; }
.eyebrow { color: var(--orator-blue); font-size: 0.75rem; font-weight: 700; letter-spacing: 0.12em; margin-bottom: 0.5rem; }
h1 { color: var(--orator-arsenic); font-size: 2rem; font-weight: 700; margin: 0 0 0.5rem; }
.devices-header > p:last-child, .this-browser, .devices-footnote { color: #52606d; }
.devices-panel { background: white; border: 1px solid var(--orator-smoke); border-radius: 0.75rem; overflow: hidden; }
.devices-summary { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 0.75rem; padding: 1.25rem 1.5rem; background: var(--orator-cloud); border-bottom: 1px solid var(--orator-smoke); }
.device-count { color: #166534; font-weight: 600; }
.this-browser { font-size: 0.875rem; }
table { width: 100%; text-align: left; border-collapse: collapse; }
th { padding: 1rem 1.5rem 0.75rem; color: #52606d; font-size: 0.8rem; font-weight: 600; }
td { padding: 1.25rem 1.5rem; border-bottom: 1px solid #edf0f2; color: var(--orator-arsenic); }
.connection-dot { display: inline-block; width: 8px; height: 8px; background: #16a34a; border-radius: 50%; margin-right: 0.75rem; }
.connection-dot.offline { background: #b45309; }
.offline-status { color: #92400e; }
.connected-status { color: #166534; }
.last-seen { display: block; color: #52606d; font-size: 0.75rem; margin-top: 0.3rem; }
.you-label { display: inline-block; margin-left: 0.65rem; color: #52606d; font-size: 0.75rem; }
.devices-empty, .devices-error { padding: 2rem 1.5rem; }
.devices-error { color: #991b1b; background: #fef2f2; margin: 1rem; border-radius: 0.5rem; }
.devices-footnote { margin: 0; padding: 1.25rem 1.5rem; font-size: 0.8rem; line-height: 1.6; }
@media (max-width: 960px) { .devices-layout { flex-direction: column; } .devices-main { width: 100%; } }
@media (max-width: 600px) { .devices-layout { padding: 1rem; } th, td { padding: 1rem; } .you-label { display: block; margin-left: 1.25rem; } }
</style>
